// DEBT-VITA™ — Bouton multi-format pour générer le rapport (PDF / Word / Excel)
import React, { useState } from "react";
import { toast } from "sonner";
import {
  FileDown,
  FileText,
  FileSpreadsheet,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { generateDebtPdf } from "@/lib/debt-report/pdf";
import { generateDebtExcel } from "@/lib/debt-report/excel";
import { generateDebtWord } from "@/lib/debt-report/word";

export default function DebtReportDownloadButton({
  project,
  params,
  results,
  narrative,
  disabled,
}) {
  const [busy, setBusy] = useState(null); // "pdf" | "xlsx" | "docx" | null

  async function handleGenerate(format) {
    if (busy) return;
    if (!results) {
      toast.error("Lancez d'abord un calcul DEBT-VITA™.");
      return;
    }
    setBusy(format);
    try {
      let filename = "";
      if (format === "pdf") {
        const r = await generateDebtPdf({ project, params, results, narrative });
        filename = r.filename;
      } else if (format === "xlsx") {
        filename = generateDebtExcel({ project, params, results, narrative });
      } else if (format === "docx") {
        filename = await generateDebtWord({ project, params, results, narrative });
      }
      toast.success(`Rapport généré : ${filename}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[debt-report] generation failed", e);
      toast.error(
        "Échec de la génération : " +
          (e?.message || "erreur inconnue, voir la console.")
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled || !results}
          className="bg-amber-600 text-white hover:bg-amber-700"
          data-testid="debt-report-download-btn"
        >
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="mr-2 h-4 w-4" />
          )}
          Générer le rapport DEBT-VITA™
          <ChevronDown className="ml-2 h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onClick={() => handleGenerate("pdf")}
          disabled={!!busy}
          data-testid="debt-report-format-pdf"
        >
          <FileText className="mr-2 h-4 w-4 text-red-600" />
          PDF (12+ pages, thème sombre)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleGenerate("docx")}
          disabled={!!busy}
          data-testid="debt-report-format-docx"
        >
          <FileText className="mr-2 h-4 w-4 text-blue-600" />
          Word (.docx)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleGenerate("xlsx")}
          disabled={!!busy}
          data-testid="debt-report-format-xlsx"
        >
          <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-600" />
          Excel (5 onglets, formules vivantes)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
